import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createChart,
  HistogramSeries,
  AreaSeries,
  LineStyle,
} from 'lightweight-charts';
import { prepareChartSeries } from '../../hooks/useLiquidationMap';
import ChartWatermark from '../ChartWatermark/ChartWatermark';
import './LiquidationMapChart.css';

// Coinglass convention: long positions liquidate when price DROPS, so they're
// drawn red (down move). Short positions liquidate when price RISES, so green
// (up move). Trader users coming from Coinglass / Coinalyze will read the
// chart the same way.
const COLOR_LONG = '#ef4444';   // red
const COLOR_SHORT = '#22c55e';  // green
const COLOR_LONG_BAR = 'rgba(239, 68, 68, 0.85)';
const COLOR_SHORT_BAR = 'rgba(34, 197, 94, 0.85)';
const COLOR_LONG_AREA_TOP = 'rgba(239, 68, 68, 0.45)';
const COLOR_LONG_AREA_BOTTOM = 'rgba(239, 68, 68, 0.02)';
const COLOR_SHORT_AREA_TOP = 'rgba(34, 197, 94, 0.45)';
const COLOR_SHORT_AREA_BOTTOM = 'rgba(34, 197, 94, 0.02)';
const COLOR_MARK_LINE = '#facc15';

// Variant styling. `real` is the unchanged baseline; `synthetic` reduces bar
// opacity to ~0.7 and switches the cumulative area outline to dashed so users
// read it as a model. `combined` uses darker shades to distinguish the
// aggregated view. Unknown variant names fall back to `real`.
export const CHART_VARIANTS = {
  real: {
    longBar: COLOR_LONG_BAR,
    shortBar: COLOR_SHORT_BAR,
    longLine: COLOR_LONG,
    shortLine: COLOR_SHORT,
    longAreaTop: COLOR_LONG_AREA_TOP,
    longAreaBottom: COLOR_LONG_AREA_BOTTOM,
    shortAreaTop: COLOR_SHORT_AREA_TOP,
    shortAreaBottom: COLOR_SHORT_AREA_BOTTOM,
    lineStyleName: 'solid',
    badgeKey: null,
    badgeFallback: null,
    badgeClass: '',
  },
  synthetic: {
    longBar: 'rgba(239, 68, 68, 0.60)',
    shortBar: 'rgba(34, 197, 94, 0.60)',
    longLine: COLOR_LONG,
    shortLine: COLOR_SHORT,
    longAreaTop: 'rgba(239, 68, 68, 0.30)',
    longAreaBottom: 'rgba(239, 68, 68, 0.01)',
    shortAreaTop: 'rgba(34, 197, 94, 0.30)',
    shortAreaBottom: 'rgba(34, 197, 94, 0.01)',
    lineStyleName: 'dashed',
    badgeKey: 'liqMap.variant_synthetic_badge',
    badgeFallback: 'modelled',
    badgeClass: 'liq-map__badge--synthetic',
  },
  combined: {
    longBar: 'rgba(220, 38, 38, 0.95)',
    shortBar: 'rgba(22, 163, 74, 0.95)',
    longLine: '#dc2626',
    shortLine: '#16a34a',
    longAreaTop: 'rgba(220, 38, 38, 0.55)',
    longAreaBottom: 'rgba(220, 38, 38, 0.02)',
    shortAreaTop: 'rgba(22, 163, 74, 0.55)',
    shortAreaBottom: 'rgba(22, 163, 74, 0.02)',
    lineStyleName: 'solid',
    badgeKey: 'liqMap.variant_combined_badge',
    badgeFallback: 'all sources',
    badgeClass: 'liq-map__badge--combined',
  },
};

export function resolveVariant(name) {
  if (typeof name === 'string' && Object.prototype.hasOwnProperty.call(CHART_VARIANTS, name)) {
    return CHART_VARIANTS[name];
  }
  return CHART_VARIANTS.real;
}

export function hasAnyBuckets(data) {
  if (!data || !Array.isArray(data.bars) || data.bars.length === 0) return false;
  for (const b of data.bars) {
    if ((Number(b.longSize) || 0) > 0) return true;
    if ((Number(b.shortSize) || 0) > 0) return true;
  }
  return false;
}

function readThemeColors(container) {
  if (typeof window === 'undefined' || !container) {
    return {
      bg: '#0a0a0a',
      text: '#a1a1aa',
      grid: 'rgba(38, 38, 38, 0.5)',
      border: '#262626',
    };
  }
  const styles = window.getComputedStyle(container);
  const bg = styles.getPropertyValue('--bg-primary').trim() || '#0a0a0a';
  const text = styles.getPropertyValue('--text-primary').trim() || '#a1a1aa';
  return {
    bg,
    text,
    grid: 'rgba(120, 120, 120, 0.15)',
    border: 'rgba(120, 120, 120, 0.3)',
  };
}

function LiquidationMapChart({ symbol, data, loading, variant = 'real' }) {
  const { t } = useTranslation();
  const style = resolveVariant(variant);
  const containerRef = useRef(null);
  const overlayRef = useRef(null);
  const chartRef = useRef(null);
  const longBarsRef = useRef(null);
  const shortBarsRef = useRef(null);
  const cumLongRef = useRef(null);
  const cumShortRef = useRef(null);
  const observerRef = useRef(null);
  // pricesRef carries the latest bucket→price mapping into the chart's
  // tickMarkFormatter closure, which is captured once at createChart time.
  const pricesRef = useRef([]);
  const markIdxRef = useRef(null);
  // Bucket-idx → chart "time" requires an offset that bumps every data
  // refresh. Lightweight-charts has an internal FormattedLabelsCache keyed
  // by time alone — never invalidated on setData — so reusing the same
  // time integers (0..N) across refreshes makes stale labels stick. We
  // shift the time space forward each push so cache lookups always miss
  // and the formatter is re-invoked with current pricesRef.
  const baseTimeRef = useRef(0);

  const [markX, setMarkX] = useState(null);

  const isEmpty = !hasAnyBuckets(data);

  // ---- create chart once on mount ----
  useEffect(() => {
    if (!containerRef.current) return undefined;
    const container = containerRef.current;
    const theme = readThemeColors(container);

    const chart = createChart(container, {
      layout: {
        background: { color: theme.bg },
        textColor: theme.text,
        fontFamily: "'SF Mono', Monaco, 'Courier New', monospace",
      },
      grid: {
        vertLines: { color: theme.grid },
        horzLines: { color: theme.grid },
      },
      rightPriceScale: { borderColor: theme.border },
      leftPriceScale: { visible: true, borderColor: theme.border },
      timeScale: {
        borderColor: theme.border,
        timeVisible: false,
        secondsVisible: false,
        // X-axis values are bucket indices. Map back to the bucket's price
        // so users see "$80 618" instead of "0, 1, 2 …" labels.
        // pricesRef is read lazily on each tick — the latest data is
        // always reflected without re-creating the chart.
        tickMarkFormatter: (time) => {
          const idx = Number(time) - baseTimeRef.current;
          const price = pricesRef.current[idx];
          if (!Number.isFinite(price)) return '';
          return formatPrice(price);
        },
      },
      // Crosshair tooltip (and any other place that surfaces the X value)
      // also wants the same price label, not a raw bucket index.
      localization: {
        timeFormatter: (time) => {
          const idx = Number(time) - baseTimeRef.current;
          const price = pricesRef.current[idx];
          if (!Number.isFinite(price)) return String(idx);
          return formatPrice(price);
        },
      },
      handleScroll: true,
      // Allow zoom (wheel + pinch) but disable price-axis drag. The Y-axes on
      // a liquidation map have no meaningful manual scale — auto-fit covers
      // it — and dragging the axis labels makes the "0" reference marker
      // appear to swim, which confuses users.
      handleScale: {
        axisPressedMouseMove: false,
        mouseWheel: true,
        pinch: true,
        axisDoubleClickReset: true,
      },
    });

    // LineStyle is captured from the imported enum at createChart time. The
    // synthetic variant uses Dashed for the cumulative area outline so users
    // visually flag it as model output rather than precise data.
    const cumLineStyle = style.lineStyleName === 'dashed'
      ? (LineStyle?.Dashed ?? 2)
      : (LineStyle?.Solid ?? 0);

    // Bars on the LEFT scale (per-bucket size in USD)
    longBarsRef.current = chart.addSeries(HistogramSeries, {
      priceScaleId: 'left',
      color: style.longBar,
      priceFormat: { type: 'volume' },
    });
    shortBarsRef.current = chart.addSeries(HistogramSeries, {
      priceScaleId: 'left',
      color: style.shortBar,
      priceFormat: { type: 'volume' },
    });

    // Cumulative AREA on the RIGHT scale — gradient fill makes the
    // "stack" of liquidations visually heavy, like Coinglass.
    cumLongRef.current = chart.addSeries(AreaSeries, {
      priceScaleId: 'right',
      lineColor: style.longLine,
      topColor: style.longAreaTop,
      bottomColor: style.longAreaBottom,
      lineStyle: cumLineStyle,
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: true,
      priceFormat: { type: 'volume' },
    });
    cumShortRef.current = chart.addSeries(AreaSeries, {
      priceScaleId: 'right',
      lineColor: style.shortLine,
      topColor: style.shortAreaTop,
      bottomColor: style.shortAreaBottom,
      lineStyle: cumLineStyle,
      lineWidth: 2,
      priceLineVisible: false,
      lastValueVisible: true,
      priceFormat: { type: 'volume' },
    });

    chartRef.current = chart;

    // Re-position the vertical mark line whenever the time scale shifts
    // (zoom/pan) or the chart resizes. The line itself is a DOM element
    // (lightweight-charts has no native vertical-line primitive in v5).
    let unsubTimeScale = null;
    try {
      const ts = chart.timeScale();
      if (ts && typeof ts.subscribeVisibleLogicalRangeChange === 'function') {
        const handler = () => updateMarkPosition();
        ts.subscribeVisibleLogicalRangeChange(handler);
        unsubTimeScale = () => {
          try { ts.unsubscribeVisibleLogicalRangeChange(handler); } catch { /* ignore */ }
        };
      }
    } catch { /* ignore — older API or test mock */ }

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const { width, height } = entry.contentRect;
          chart.applyOptions({ width, height: Math.max(height, 280) });
        }
        updateMarkPosition();
      });
      observer.observe(container);
      observerRef.current = observer;
    }

    return () => {
      if (unsubTimeScale) unsubTimeScale();
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      try {
        chart.remove();
      } catch {
        // ignore
      }
      chartRef.current = null;
      longBarsRef.current = null;
      shortBarsRef.current = null;
      cumLongRef.current = null;
      cumShortRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- push data on every change ----
  useEffect(() => {
    if (!chartRef.current) return;
    const series = prepareChartSeries(data);
    pricesRef.current = series.prices;

    // Bump time base BEFORE building setData payloads. The bump must exceed
    // the largest possible bucket count (500) so two consecutive refreshes
    // never reuse the same time integer — that's what defeats the
    // FormattedLabelsCache.
    baseTimeRef.current += 1000;
    const baseTime = baseTimeRef.current;
    const shift = (arr) => arr.map((p) => ({ time: p.time + baseTime, value: p.value }));

    if (longBarsRef.current) longBarsRef.current.setData(shift(series.longBars));
    if (shortBarsRef.current) shortBarsRef.current.setData(shift(series.shortBars));
    if (cumLongRef.current) cumLongRef.current.setData(shift(series.cumLong));
    if (cumShortRef.current) cumShortRef.current.setData(shift(series.cumShort));

    // Find bucket index closest to the live mark price — that's where the
    // vertical line lands. Prefer the direct `(currentPrice - windowLow) /
    // bucketSize` formula (same shape the server uses to anchor cumulative
    // walks in combinedLiquidationMap.js) — it's O(1) and immune to a stray
    // NaN/null in bars[i].price. The previous linear-min-diff search was
    // sticky: if `series.prices[i] - data.currentPrice` produced NaN for
    // every later bar, `NaN < bestDiff` was always false and bestIdx stayed
    // pinned to its initial 0, dropping the yellow mark line on the leftmost
    // visible bucket regardless of where the real mark price sat.
    if (
      data
      && typeof data.currentPrice === 'number'
      && Number.isFinite(data.currentPrice)
      && data.currentPrice > 0
      && series.prices.length > 0
    ) {
      let bestIdx = -1;
      const bucketSize = Number(data.bucketSize);
      const firstPrice = Number(series.prices[0]);
      if (Number.isFinite(bucketSize) && bucketSize > 0 && Number.isFinite(firstPrice)) {
        const windowLow = firstPrice - bucketSize / 2;
        const idx = Math.floor((data.currentPrice - windowLow) / bucketSize);
        if (idx >= 0 && idx < series.prices.length) bestIdx = idx;
      }
      // Fallback when bucketSize is missing or the direct formula lands
      // outside the array — linear search, but skip non-finite prices so a
      // single bad bar can't pin bestIdx to its initial value.
      if (bestIdx < 0) {
        let bestDiff = Infinity;
        for (let i = 0; i < series.prices.length; i += 1) {
          const p = series.prices[i];
          if (!Number.isFinite(p)) continue;
          const d = Math.abs(p - data.currentPrice);
          if (d < bestDiff) {
            bestDiff = d;
            bestIdx = i;
          }
        }
      }
      markIdxRef.current = bestIdx >= 0 ? bestIdx : null;
    } else {
      markIdxRef.current = null;
    }

    if (chartRef.current && series.longBars.length > 0) {
      try {
        chartRef.current.timeScale().fitContent();
      } catch {
        // ignore
      }
    }
    // After setData + fitContent lightweight-charts hasn't yet recomputed
    // its layout — calling timeToCoordinate() in this same tick returns
    // pixel positions from the *previous* visible range. That caused the
    // mark line to stick to the old currentPrice's bucket while the label
    // (which renders data.currentPrice directly) updated to the new value.
    // Defer one frame so the timeScale is in sync with the new bars.
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(updateMarkPosition);
    } else {
      updateMarkPosition();
    }
    // `t` is intentionally not in deps: the effect body never reads it
    // (only the JSX does). Some i18n setups return a fresh `t` every render,
    // which would otherwise re-fire setData on every state update.
  }, [data]);

  // Translate the bucket index into a pixel X coordinate so the DOM
  // overlay can position itself across the chart's vertical axis.
  function updateMarkPosition() {
    const chart = chartRef.current;
    const idx = markIdxRef.current;
    if (chart == null || idx == null) {
      setMarkX(null);
      return;
    }
    try {
      const ts = chart.timeScale();
      if (!ts || typeof ts.timeToCoordinate !== 'function') {
        setMarkX(null);
        return;
      }
      const x = ts.timeToCoordinate(idx + baseTimeRef.current);
      if (typeof x !== 'number' || !Number.isFinite(x)) {
        setMarkX(null);
        return;
      }
      // timeToCoordinate returns X relative to the chart's plot area, but the
      // DOM overlay is positioned inside .liq-map__canvas (the container,
      // which also holds the left price scale to the left of the plot). The
      // left scale eats ~30-60px on the container's left edge depending on
      // label width; without compensating, the yellow line drifts that many
      // pixels left of its bucket — ~3 buckets at a 200-bucket / 1800px-wide
      // layout. Add the left scale's width back so the overlay's `left`
      // value lands at the same container pixel as the bar at that time.
      let leftAxisOffset = 0;
      try {
        const leftScale = typeof chart.priceScale === 'function' ? chart.priceScale('left') : null;
        if (leftScale && typeof leftScale.width === 'function') {
          const w = leftScale.width();
          if (Number.isFinite(w) && w > 0) leftAxisOffset = w;
        }
      } catch {
        // Older lightweight-charts versions / test mocks may not expose
        // priceScale('left'). Falling through with offset=0 keeps the
        // overlay aligned for those (and matches pre-fix behaviour).
      }
      setMarkX(x + leftAxisOffset);
    } catch {
      setMarkX(null);
    }
  }

  const variantName = Object.prototype.hasOwnProperty.call(CHART_VARIANTS, variant)
    ? variant
    : 'real';

  return (
    <div
      className={`liq-map liq-map--variant-${variantName} ${loading ? 'liq-map--loading' : ''}`}
      data-variant={variantName}
    >
      <div className="liq-map__legend">
        <span className="liq-map__legend-item">
          <span className="liq-map__swatch" style={{ background: style.longLine }} />
          {t('liqMap.long', 'Long liquidations')}
        </span>
        <span className="liq-map__legend-item">
          <span className="liq-map__swatch" style={{ background: style.shortLine }} />
          {t('liqMap.short', 'Short liquidations')}
        </span>
        {style.badgeKey && (
          <span
            className={`liq-map__badge ${style.badgeClass}`}
            data-testid="liq-map-variant-badge"
          >
            {t(style.badgeKey, style.badgeFallback)}
          </span>
        )}
        {symbol && <span className="liq-map__symbol">{symbol}</span>}
      </div>
      <div className="liq-map__canvas" ref={containerRef}>
        {isEmpty && !loading && (
          <div className="liq-map--empty">
            {t('liqMap.empty', 'No liquidation data for this symbol yet.')}
          </div>
        )}
        {loading && <div className="liq-map__loading-overlay">{t('liqMap.loading', 'Loading liquidation map…')}</div>}
        {markX != null && data?.currentPrice != null && (
          <div
            ref={overlayRef}
            className="liq-map__mark-line"
            style={{ left: `${markX}px` }}
            aria-hidden="true"
          >
            <span className="liq-map__mark-label">
              {t('liqMap.current_price', 'Mark')}: {formatPrice(data.currentPrice)}
            </span>
          </div>
        )}
        <ChartWatermark />
      </div>
    </div>
  );
}

export function formatPrice(price) {
  if (!Number.isFinite(price)) return '';
  if (price >= 1000) return `$${Math.round(price).toLocaleString()}`;
  if (price >= 1) return `$${price.toFixed(2)}`;
  return `$${price.toPrecision(4)}`;
}

export default React.memo(LiquidationMapChart, (prev, next) => {
  return (
    prev.symbol === next.symbol
    && prev.loading === next.loading
    && prev.variant === next.variant
    && (prev.data?.generatedAt ?? null) === (next.data?.generatedAt ?? null)
  );
});
